import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Phone, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AccountDetails() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { lang } = useLang();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Split phone into country code (+966) and local part
  const rawPhone = user?.phone || "";
  let countryCode = "966";
  let localPhone = "";
  if (rawPhone.startsWith("+")) {
    const digits = rawPhone.slice(1);
    if (digits.startsWith("966")) { countryCode = "966"; localPhone = digits.slice(3); }
    else { countryCode = digits.slice(0, 3); localPhone = digits.slice(3); }
  } else {
    localPhone = rawPhone;
  }

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      await logout();
      toast.success(lang === "ar" ? "تم حذف الحساب" : "Account deleted");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "تعذر حذف الحساب" : "Failed to delete account"));
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen">
      {/* Top bar */}
      <div className="px-5 pt-6 pb-2 flex items-center">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="account-back-btn">
          <ChevronRight className="w-7 h-7 text-[#E53935]" strokeWidth={2.5} />
        </button>
      </div>

      {/* Title */}
      <h1 className="font-display text-3xl font-black text-[#1A1A1A] px-5 pt-2 pb-8" data-testid="account-title">
        {lang === "ar" ? "حسابي" : "My account"}
      </h1>

      {/* Fields */}
      <div className="px-5 space-y-3">
        {/* Phone — split into country code and number */}
        <div className="flex gap-3" dir="rtl">
          <div className="flex-1 flex items-center gap-3 bg-white rounded-2xl px-4 py-4 border border-[#EFEFEF]">
            <Phone className="w-5 h-5 text-[#E0A33A]" strokeWidth={2.2} />
            <span className="flex-1 text-base font-semibold text-[#1A1A1A] text-center" dir="ltr" data-testid="account-phone">
              {localPhone || "—"}
            </span>
          </div>
          <div className="w-[120px] flex items-center justify-center gap-2 bg-white rounded-2xl px-3 py-4 border border-[#EFEFEF]" data-testid="account-country">
            <span className="text-base font-semibold text-[#1A1A1A]">{countryCode}</span>
            <span className="text-2xl leading-none">🇸🇦</span>
          </div>
        </div>

        {/* Name */}
        <div className="flex items-center gap-3 bg-white rounded-2xl px-4 py-4 border border-[#EFEFEF]">
          <UserIcon className="w-5 h-5 text-[#E0A33A]" strokeWidth={2.2} />
          <span className="flex-1 text-base font-semibold text-[#1A1A1A] text-center" data-testid="account-name">
            {user?.name || "—"}
          </span>
        </div>
      </div>

      {/* Delete account link (fixed-ish, large breathing room) */}
      <div className="text-center pt-32 pb-10">
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className="text-[#E53935] font-bold text-base"
          data-testid="account-delete-btn"
        >
          {lang === "ar" ? "حذف الحساب" : "Delete account"}
        </button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent dir={lang === "ar" ? "rtl" : "ltr"}>
          <AlertDialogHeader>
            <AlertDialogTitle>{lang === "ar" ? "تأكيد حذف الحساب" : "Confirm account deletion"}</AlertDialogTitle>
            <AlertDialogDescription>
              {lang === "ar"
                ? "سيتم حذف بياناتك بشكل نهائي ولن يمكن استرجاعها. هل أنت متأكد؟"
                : "Your data will be permanently deleted and cannot be recovered. Are you sure?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">{lang === "ar" ? "إلغاء" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-[#E53935] hover:bg-[#C62828] text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "..." : (lang === "ar" ? "حذف" : "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
